import {
  RiDashboardLine,
  RiPlaneLine,
  RiFileTextLine,
  RiTeamLine,
  RiCompassLine,
  RiStarLine,
  RiSettings3Line
} from '@remixicon/react';

const FinancialsIcon = ({ className }) => (
  <svg className={className} width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.80451 9.39225L4.35601 6.84075L6.47701 8.9625L8.84476 6.59475L7.50001 5.25H11.25V9L9.90526 7.65525L6.47701 11.0835L4.35601 8.9625L2.49976 10.818C3.34232 12.0861 4.63547 12.9866 6.11718 13.3368C7.59889 13.687 9.15832 13.4608 10.4795 12.704C11.8006 11.9473 12.7847 10.7166 13.2322 9.26131C13.6797 7.80604 13.5573 6.23505 12.8897 4.8667C12.2221 3.49835 11.0592 2.43497 9.63679 1.89206C8.21434 1.34915 6.6387 1.3673 5.22915 1.94285C3.81959 2.5184 2.68156 3.60829 2.04566 4.99167C1.40975 6.37504 1.32355 7.94843 1.80451 9.393V9.39225ZM0.651006 10.5607L0.643506 10.5532L0.646506 10.5502C0.218953 9.59026 -0.00134077 8.55089 6.13933e-06 7.5C6.13933e-06 3.35775 3.35776 0 7.50001 0C11.6423 0 15 3.35775 15 7.5C15 11.6423 11.6423 15 7.50001 15C4.44751 15 1.82251 13.1775 0.651006 10.5607Z" fill="currentColor"/>
  </svg>
);

// App constants
export const APP_NAME = 'TrustJet';
export const APP_VERSION = '1.0.0';

// Routes with titles
export const ROUTES = {
  HOME: { path: '/', title: 'Home' },
  SIGN_IN: { path: '/signin', title: 'Sign In' },
  SIGN_UP: { path: '/signup', title: 'Sign Up' },
  DASHBOARD: { path: '/dashboard', title: 'Dashboard' },
  PROFILE: { path: '/profile', title: 'Profile' },
  FORGOT_PASSWORD: { path: '/forgot-password', title: 'Forgot Password' },
  RESET_PASSWORD: { path: '/reset-password', title: 'Reset Password' },
  MFA_ENROLL: { path: '/mfa-enroll', title: 'MFA Enrollment' },
  MFA_VERIFY: { path: '/mfa-verify', title: 'MFA Verification' },
  EMAIL_CONFIRMATION: { path: '/email-confirmation', title: 'Email Confirmation' },
  AUTH_CALLBACK: { path: '/auth-callback', title: 'Signing In' },
  REGISTER_CALLBACK: { path: '/register-callback', title: 'Registering' },
  PROFILE_SETUP: { path: '/profile-setup', title: 'Profile Setup' },
  ONBOARDING: { path: '/onboarding', title: 'Onboarding' },
  REGISTER_TEAM: { path: '/register/team', title: 'Team Registration' },
  PARSE_PILOT: { path: '/parse-pilot', title: 'Parse Pilot' },
  BIDS: { path: '/bids', title: 'Bids' },
  BID_DETAIL: { path: '/bids/:id', title: 'Bid Details' },
  MESSAGES: { path: '/messages', title: 'Messages' },
  TRACK: { path: '/track', title: 'Track Flights' },
  CLIENTS: { path: '/clients', title: 'Clients' },
  FINANCIALS: { path: '/financials', title: 'Financials' },
  FINANCIAL_REPORTS: { path: '/financials/reports', title: 'Financial Reports' },
  SEARCH_AIRCRAFT: { path: '/search-aircraft', title: 'Search Aircraft' },
  AIRCRAFT_DETAIL: { path: '/aircraft/:tailNumber', title: 'Aircraft Details' },
  QUOTES: { path: '/quotes', title: 'Quotes' },
  QUOTE_DETAIL: { path: '/quotes/:id', title: 'Quote Details' },
  GENERATE_QUOTE: { path: '/quotes/generate', title: 'Generate Quote' },
  CHECKOUT: { path: '/checkout', title: 'Checkout' },
  BOOKING_CONFIRMED: { path: '/booking-confirmed', title: 'Booking Confirmed' },
  WIRE_TRANSFER_CONFIRMED: { path: '/wire-transfer-confirmed', title: 'Wire Transfer Confirmed' },
  TAILS: { path: '/tails', title: 'Available Tails' },
  QUOTE_PREVIEW: { path: '/quotepreview', title: 'Quote Preview' },
  OPERATOR_PROFILE: { path: '/operators/:id', title: 'Operator Profile' },
  TRIP_SHEET: { path: '/trip-sheet', title: 'Build Trip Sheet' },
  CLIENT_TRIP_SHEET: { path: '/tripsheet/:quoteId', title: 'Passenger Details' },
  GENERATE_TOKEN: { path: '/generate-token', title: 'Generate Token' },
  TAX_REPORTS: { path: '/reports/taxes', title: 'Reporting' },
  BROKER_MANAGEMENT: { path: '/broker-management', title: 'Broker Management' },
  ADD_CLIENT: { path: '/clients/add', title: 'Add Client' },
  EDIT_CLIENT: { path: '/clients/:id/edit', title: 'Edit Client' },
  CLIENT_DETAIL: { path: '/clients/:id', title: 'Client Details' },
  TRACK_FLIGHT: { path: '/track/:flightId/:tailNumber?', title: 'Track Flights' }
};

/**
 * Get page title from current URL path
 * @param {string} pathname - Current URL pathname
 * @returns {string} Page title or 'Page' as fallback
 */
export const getPageTitle = (pathname) => {
  // Direct match first
  for (const route of Object.values(ROUTES)) {
    if (route.path === pathname) {
      return route.title;
    }
  }

  // Match dynamic routes (e.g., /bids/:id, /aircraft/:tailNumber)
  for (const route of Object.values(ROUTES)) {
    if (route.path.includes(':')) {
      // Convert route pattern to regex
      const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      
      if (regex.test(pathname)) {
        return route.title;
      }
    }
  }

  // Fallback
  return 'Page';
};

// Public routes that don't require authentication and should use AuthLayout
export const PUBLIC_ROUTES = [
  ROUTES.HOME.path,
  ROUTES.SIGN_IN.path,
  ROUTES.SIGN_UP.path,
  ROUTES.FORGOT_PASSWORD.path,
  ROUTES.RESET_PASSWORD.path,
  ROUTES.MFA_ENROLL.path,
  ROUTES.MFA_VERIFY.path,
  ROUTES.EMAIL_CONFIRMATION.path,
  ROUTES.AUTH_CALLBACK.path,
  ROUTES.REGISTER_CALLBACK.path,
  ROUTES.PROFILE_SETUP.path,
  ROUTES.ONBOARDING.path,
  ROUTES.REGISTER_TEAM.path
  // Add more public routes as needed
];

// Sidebar Navigations
export const SIDEBAR_MENUS = [
  { id: 'dashboard', icon: RiDashboardLine, label: 'Dashboard', route: ROUTES.DASHBOARD.path },
  { id: 'track', icon: RiPlaneLine, label: 'Track', route: ROUTES.TRACK.path },
  { id: 'quotes', icon: RiFileTextLine, label: 'Quotes', route: ROUTES.QUOTES.path },
  { id: 'clients', icon: RiTeamLine, label: 'Clients', route: ROUTES.CLIENTS.path },
  { id: 'financials', icon: FinancialsIcon, label: 'Financials', route: ROUTES.FINANCIAL_REPORTS.path },
  { id: 'broker-management', icon: RiSettings3Line, label: 'Brokers', route: ROUTES.BROKER_MANAGEMENT.path, roles: ['admin'] },
];

// API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },
  USER: {
    PROFILE: '/user/profile',
    UPDATE_PROFILE: '/user/profile',
  },
};

// Form validation
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^\+?[\d\s\-\(\)]+$/,
};

// UI constants
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
};

export const NOTIFICATION_DURATION = {
  SHORT: 3000,
  MEDIUM: 5000,
  LONG: 8000,
};

// Storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
};
